import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useState } from 'react'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import { Switch } from '@/common/ui/switch'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Skeleton } from '@/common/ui/skeleton'
import { Edit, Plus, Trash2 } from 'lucide-react'
import { AlertModal } from '@/common/components/AlertModal'
import useQueryGetAllAutoMessages from '../../api/hooks/useQueryGetAllAutoMessages'
import useMutationUpdateAutoMessage from '../../api/hooks/useMutationUpdateAutoMessage'
import useMutationDeleteAutoMessage from '../../api/hooks/useMutationDeleteAutoMessage'
import { getConditionLabel, getChannelLabel } from '../../utils/conditionLabels'
import { MessageChannel, LineTransactionType } from '../../types'

import type { GetAutoMessageResponse } from '../../types'

const ManageAutoMessagesPage = () => {
	const navigate = useNavigate()
	const {
		data: autoMessages,
		isLoading,
		refetch,
	} = useQueryGetAllAutoMessages()

	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
		null,
	)

	const updateAutoMessageMutation = useMutationUpdateAutoMessage({
		onSuccess: () => {
			toast.success('อัปเดตสถานะสำเร็จ')
			refetch()
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการอัปเดตสถานะ')
		},
	})

	const deleteAutoMessageMutation = useMutationDeleteAutoMessage({
		onSuccess: () => {
			toast.success('ลบข้อความอัตโนมัติสำเร็จ')
			setDeleteModalOpen(false)
			setSelectedMessageId(null)
			refetch()
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการลบข้อความอัตโนมัติ')
		},
	})

	const handleToggleActive = (id: string, currentStatus: boolean) => {
		updateAutoMessageMutation.mutate({
			id,
			data: {
				isActive: !currentStatus,
			},
		})
	}

	const handleDelete = () => {
		if (!selectedMessageId) return
		deleteAutoMessageMutation.mutate(selectedMessageId)
	}

	const getChannelBadges = (autoMessage: GetAutoMessageResponse) => {
		const channels: JSX.Element[] = []

		if (autoMessage.autoSmsTransaction) {
			channels.push(
				<Badge key="sms" variant="default">
					{getChannelLabel(MessageChannel.SMS)}
				</Badge>,
			)
		}

		if (autoMessage.autoLineTransaction) {
			// Check if it's a flex hot game or regular LINE message
			if (
				autoMessage.lineTransactionType === LineTransactionType.FLEX_HOT_GAME
			) {
				channels.push(
					<Badge
						key="line-hot-game"
						variant="default"
						className="bg-purple-600"
					>
						{getChannelLabel(MessageChannel.LINE_HOT_GAME)}
					</Badge>,
				)
			} else {
				channels.push(
					<Badge key="line" variant="default" className="bg-green-600">
						{getChannelLabel(MessageChannel.LINE)}
					</Badge>,
				)
			}
		}

		return channels.length > 0 ? (
			channels
		) : (
			<span className="text-muted-foreground">-</span>
		)
	}

	return (
		<PageContainer>
			<div className="space-y-4 px-4 sm:px-0">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
					<Heading
						title="จัดการข้อความอัตโนมัติ"
						description="รายการข้อความที่ส่งอัตโนมัติทั้งหมด"
					/>
					<Button onClick={() => navigate('/operator/messaging/auto/create')}>
						<Plus className="mr-2 h-4 w-4" />
						สร้างข้อความอัตโนมัติ
					</Button>
				</div>

				<Separator className="my-4 sm:my-6" />

				{isLoading ? (
					<div className="space-y-3">
						{[...Array(5)].map((_, i) => (
							<Skeleton key={i} className="h-12 w-full" />
						))}
					</div>
				) : (
					<div className="rounded-md border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>ข้อความ</TableHead>
									<TableHead>ช่องทาง</TableHead>
									<TableHead>เงื่อนไข</TableHead>
									<TableHead>สถานะ</TableHead>
									<TableHead>วันที่สร้าง</TableHead>
									<TableHead className="text-right">จัดการ</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{autoMessages && autoMessages.length > 0 ? (
									autoMessages
										.filter((msg) => !msg.isDeleted)
										.map((autoMessage) => (
											<TableRow key={autoMessage.id}>
												<TableCell className="max-w-xs">
													<div className="truncate" title={autoMessage.message}>
														{autoMessage.message}
													</div>
												</TableCell>
												<TableCell>
													<div className="flex gap-2">
														{getChannelBadges(autoMessage)}
													</div>
												</TableCell>
												<TableCell>
													{getConditionLabel(
														autoMessage.autoMessageGroupCondition,
													)}
												</TableCell>
												<TableCell>
													<div className="flex items-center gap-2">
														<Switch
															checked={autoMessage.isActive}
															onCheckedChange={() =>
																handleToggleActive(
																	autoMessage.id,
																	autoMessage.isActive,
																)
															}
														/>
														<span className="text-sm">
															{autoMessage.isActive
																? 'เปิดใช้งาน'
																: 'ปิดใช้งาน'}
														</span>
													</div>
												</TableCell>
												<TableCell>
													{format(
														new Date(autoMessage.createdAt),
														'dd MMM yyyy HH:mm',
														{ locale: th },
													)}
												</TableCell>
												<TableCell className="text-right">
													<div className="flex justify-end gap-2">
														<Button
															variant="ghost"
															size="icon"
															onClick={() =>
																navigate(
																	`/operator/messaging/auto/${autoMessage.id}`,
																)
															}
														>
															<Edit className="h-4 w-4" />
														</Button>
														<Button
															variant="ghost"
															size="icon"
															onClick={() => {
																setSelectedMessageId(autoMessage.id)
																setDeleteModalOpen(true)
															}}
														>
															<Trash2 className="h-4 w-4 text-red-600" />
														</Button>
													</div>
												</TableCell>
											</TableRow>
										))
								) : (
									<TableRow>
										<TableCell colSpan={6} className="h-24 text-center">
											ไม่พบข้อมูล
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
				)}
			</div>

			<AlertModal
				isOpen={deleteModalOpen}
				onClose={() => {
					setDeleteModalOpen(false)
					setSelectedMessageId(null)
				}}
				onConfirm={handleDelete}
				loading={deleteAutoMessageMutation.isPending}
			/>
		</PageContainer>
	)
}

const GuardedManageAutoMessagesPage = withUserPermission(
	ManageAutoMessagesPage,
	{
		permissionType: PermissionType.Read,
		permissionKey: [PermissionKeys.MANAGE_SMS],
	},
)

export { GuardedManageAutoMessagesPage as ManageAutoMessagesPage }
